import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  WorkflowClonePayload,
  WorkflowCreationAnswer,
  WorkflowCreationParams,
  WorkflowInfo,
  WorkflowModel,
  WorkflowPresets,
  WorkflowSerialized,
} from '@selfai-platform/pipeline-common';
import { PipelineConfigService } from '@selfai-platform/shared';
import { map, Observable } from 'rxjs';
import { WorkflowApi, WorkflowApiUpdateInfo } from '../models';

const API_TYPE = 'batch';

@Injectable({
  providedIn: 'root',
})
export class WorkflowApiService {
  private apiurl: string;
  private apiVersion: string;
  private readonly pathWorkflows = '/workflows';

  constructor(
    private readonly http: HttpClient,
    private readonly pipelineConfigService: PipelineConfigService,
  ) {
    const config = this.pipelineConfigService.getConfig();
    this.apiurl = `${config.hosts.api}/${config.versions.url}`;
    this.apiVersion = config.versions.api;
  }

  getAllWorkflows(): Observable<WorkflowInfo[]> {
    return this.http.get<WorkflowInfo[]>(`${this.apiurl}${this.pathWorkflows}`);
  }

  getWorkflow(workflowId: string): Observable<WorkflowModel> {
    return this.http.get<WorkflowModel>(`${this.apiurl}${this.pathWorkflows}/${workflowId}`);
  }

  createWorkflow(params: WorkflowCreationParams): Observable<WorkflowCreationAnswer> {
    const data = {
      metadata: {
        type: API_TYPE,
        apiVersion: this.apiVersion,
      },
      workflow: {
        nodes: [] as unknown[],
        connections: [] as unknown[],
      },
      thirdPartyData: {
        gui: {
          name: params.name,
          description: params.description,
          isPublic: params.isPublic,
        },
      },
    };

    return this.http.post<WorkflowCreationAnswer>(`${this.apiurl}${this.pathWorkflows}`, data);
  }

  cloneWorkflow(workflowToClone: WorkflowClonePayload): Observable<WorkflowCreationAnswer> {
    const data: WorkflowCreationParams & { isPublic: boolean } = {
      description: workflowToClone.description,
      name: workflowToClone.name,
      isPublic: workflowToClone.isPublic,
    };

    return this.http.post<WorkflowCreationAnswer>(
      `${this.apiurl}${this.pathWorkflows}/${workflowToClone.id}/clone`,
      data,
    );
  }

  deleteWorkflow(workflowId: string): Observable<string> {
    return this.http.delete(`${this.apiurl}${this.pathWorkflows}/${workflowId}`, {
      responseType: 'text',
    });
  }

  updateWorkflow(serializedWorkflow: WorkflowSerialized, loadTimestamp?: Date): Observable<void> {
    const data = {
      workflowId: serializedWorkflow.id,
      workflow: { ...serializedWorkflow },
    };

    data.workflow.metadata = {
      type: API_TYPE,
      apiVersion: this.apiVersion,
    };

    return this.http.post<void>(`${this.apiurl}${this.pathWorkflows}`, data);
  }

  cloneNotebookNode(workflowId: string, sourceNodeId: string, destinationNodeId: string) {
    return this.http.post<unknown>(
      `${this.apiurl}${this.pathWorkflows}/${workflowId}/notebook/${sourceNodeId}/copy/${destinationNodeId}`,
      null,
    );
  }

  getPresetByWorkflowId(workflowId: string): Observable<WorkflowPresets> {
    return this.http.get<WorkflowPresets>(`${this.apiurl}${this.pathWorkflows}/${workflowId}/preset`);
  }

  bindPresetToWorkflow(presetId: string, workflowId: string): Observable<string> {
    const data = {
      id: workflowId,
      presetId: presetId,
    };

    return this.http.post(`${this.apiurl}${this.pathWorkflows}/${workflowId}/preset`, data, {
      headers: {
        Accept: 'text/plain',
      },
      responseType: 'text',
    });
  }

  loadWorkflow(workflowId: string): Observable<WorkflowApi> {
    return this.http.get<WorkflowApi>(`${this.apiurl}${this.pathWorkflows}/${workflowId}`);
  }

  getUploadWorkflowMethodUrl() {
    return `${this.apiurl}${this.pathWorkflows}/upload`;
  }

  updateWorkflowInfo(workflowId: string, updateInfo: WorkflowApiUpdateInfo): Observable<string> {
    return this.http
      .put(`${this.apiurl}${this.pathWorkflows}/${workflowId}/updateInfo`, updateInfo, { responseType: 'text' })
      .pipe(map((resp) => resp));
  }

  private getDownloadWorkflowMethodUrl(workflowId: string, includeDatasources: boolean): string {
    return `${this.apiurl}${
      this.pathWorkflows
    }/${workflowId}/download?format=json&export-datasources=${!!includeDatasources}`;
  }
}
